import { CheckCircle2, UploadCloud } from 'lucide-react';
import { useId, useRef, type ChangeEvent } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

export interface DropzoneProps {
  label: string;
  /** Bold title (e.g. "Upload Company Logo"): inside the zone for rect, beside it for circle. */
  title?: string;
  /** Grey hint under the title (e.g. "PNG or JPG, square recommended, max 2MB"). */
  hint?: string;
  variant?: 'rect' | 'circle';
  state?: 'idle' | 'uploading' | 'done' | 'error';
  /** Shown in the done state. */
  fileName?: string;
  /** Pre-formatted size (e.g. "4.2 MB"), shown beside fileName in the done state. */
  fileSize?: string;
  /**
   * Whole-percent upload progress, 0-100. Omit it to keep the indeterminate
   * spinner - which is the correct fallback when the transfer reports no
   * total, not a reason to show a bar stuck at 0%.
   */
  progress?: number;
  accept?: string;
  onSelect: (file: File) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * Dashed upload dropzone (click-to-browse). `rect` is the wide card zone
 * (logo / pitch deck); `circle` is the small round founder-photo zone.
 */
export function Dropzone({
  label,
  title,
  hint,
  variant = 'rect',
  state = 'idle',
  fileName,
  fileSize,
  progress,
  accept,
  onSelect,
  error,
  disabled = false,
}: DropzoneProps) {
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onSelect(file);
    event.target.value = ''; // allow re-selecting the same file
  };

  // A determinate bar only where there is both a percentage and room for it.
  // The circle variant keeps the spinner: it is a 5MB avatar zone 16 units
  // wide, so a bar would not fit and would not earn its place if it did.
  const showProgressBar = variant === 'rect' && typeof progress === 'number';

  const icon =
    state === 'uploading' ? (
      // The bar replaces the spinner rather than joining it - two
      // simultaneous progress indicators for one transfer read as two.
      showProgressBar ? null : <LoadingSpinner />
    ) : state === 'done' ? (
      <CheckCircle2 aria-hidden="true" className="text-dkgreen" size={variant === 'rect' ? 28 : 18} />
    ) : (
      <UploadCloud aria-hidden="true" className="text-green" size={variant === 'rect' ? 28 : 18} />
    );

  const zoneClasses =
    variant === 'rect'
      ? 'flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed px-6 py-10'
      : 'flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed';

  return (
    <div className={`flex flex-col gap-1 ${variant === 'rect' ? 'h-full' : ''}`.trim()}>
      {/* The circle has no room for text inside, so its title and hint sit
          beside it - a bare circle did not say what it was for (MYN-271). */}
      <div className={variant === 'circle' ? 'flex items-center gap-4' : 'contents'}>
        <button
          type="button"
          aria-label={label}
          aria-describedby={error ? errorId : undefined}
          disabled={disabled || state === 'uploading'}
          onClick={() => inputRef.current?.click()}
          className={`${zoneClasses} ${
            state === 'error' ? 'border-danger' : 'border-green/60 hover:border-green'
          } bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dkgreen disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {icon}
          {variant === 'rect' && title && (
            <span className="font-sans text-sm font-semibold text-navy">
              {state === 'done' && fileName ? fileName : title}
            </span>
          )}
          {/* In the done state the size replaces the type/cap hint: once a file
              is attached, what it weighs is more useful than what was allowed. */}
          {variant === 'rect' && state === 'done' && fileSize && (
            <span className="text-xs text-slate">{fileSize}</span>
          )}
          {variant === 'rect' && state !== 'done' && hint && (
            <span className="text-xs text-slate">{hint}</span>
          )}
          {variant === 'rect' && state === 'uploading' && showProgressBar && (
            <div className="flex w-full max-w-xs flex-col gap-1">
              <div
                role="progressbar"
                aria-label={`${label} progress`}
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-1.5 w-full overflow-hidden rounded-full bg-green/20"
              >
                <div
                  className="h-full rounded-full bg-dkgreen transition-[width] duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-slate">{progress}%</span>
            </div>
          )}
        </button>
        {variant === 'circle' && (title || hint) && (
          <div className="flex min-w-0 flex-col gap-0.5">
            {title && <span className="font-sans text-sm font-semibold text-navy">{title}</span>}
            {hint && <span className="text-xs text-slate">{hint}</span>}
          </div>
        )}
      </div>
      {/* The circle zone has no room for the bar the rect variant gets, but it
          still needs to say a transfer is running - otherwise the only signal
          is a spinner, which is what let a save land mid-upload. Mirrors
          AvatarUploadField's caption. */}
      {variant === 'circle' && state === 'uploading' && (
        <span className="text-center text-[10px] text-green">
          {typeof progress === 'number' ? `Uploading ${progress}%` : 'Uploading…'}
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />
      {error && (
        <p id={errorId} role="alert" className="pl-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
